import React, { useState, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Checkbox,
  Collapsible,
  Flex,
  FormElement,
  Select,
} from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import { modalBulkRemoveFromGroupsState } from 'Pages/RecipientsList/recoil/state/modals'

import {
  modalBulkRemoveFromGroupsAddGroupCallback,
  modalBulkRemoveFromGroupsChangeCallback,
  modalBulkRemoveFromGroupsToggleOptAddToGroupCallback,
  modalBulkRemoveFromGroupsToggleOptSetAsTestGroupCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalBulkRemoveFromGroups'

import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/RecipientsList/recoil/services/asyncSelectGroups'

const InputAddToGroup: FC = () => {
  const intl = useIntl()

  const [forceCollapse, setForceCollapse] = useState(false)

  const {
    optAddToGroup,
    addToGroupId,
    isAddToGroupIdDirty,
    optSetAsTestGroup,
  } = useRecoilValue(modalBulkRemoveFromGroupsState)

  const addGroup = useRecoilCallback(
    modalBulkRemoveFromGroupsAddGroupCallback,
    [],
  )
  const modalStateChange = useRecoilCallback(
    modalBulkRemoveFromGroupsChangeCallback,
    [],
  )

  const toggleOptAddToGroup = useRecoilCallback(
    modalBulkRemoveFromGroupsToggleOptAddToGroupCallback,
    [],
  )

  const toggleOptSetAsTestGroup = useRecoilCallback(
    modalBulkRemoveFromGroupsToggleOptSetAsTestGroupCallback,
    [],
  )

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'modal-add-group',
      value: addToGroupId,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleSelectGroupChange = (v: number): void => {
    void modalStateChange({
      addToGroupId: v,
      isAddToGroupIdDirty: true,
    })
  }

  const handleSelectGroupExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleSelectGroupSubmit = async (s: string): Promise<void> => {
    await addGroup(s)

    setForceCollapse(true)

    setTimeout(() => {
      setForceCollapse(false)
    }, 250)
  }

  const showSelectGroupFeedback =
    (addToGroupId === undefined || addToGroupId == null) && isAddToGroupIdDirty

  return (
    <Flex flexDirection="column">
      <Checkbox
        checked={optAddToGroup}
        data-mu-test="opt-add-to-group"
        label={intl.formatMessage({
          id: 'Load.RecipientsList.AddToGroup',
        })}
        onChange={toggleOptAddToGroup}
      />

      <Collapsible collapsed={!optAddToGroup}>
        <Flex flexDirection="column" gap="16px" padding="8px 0 0 26px">
          <FormElement
            hint={intl.formatMessage(
              {
                id: 'Load.RecipientsList.SelectGroupAddGroupHint',
              },
              {
                b: (chunks) => <strong>{chunks}</strong>,
              },
            )}
            variant={showSelectGroupFeedback ? 'danger' : undefined}
            feedback={
              showSelectGroupFeedback
                ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
                : undefined
            }
          >
            <Select
              value={addToGroupId}
              options={options}
              loading={loading}
              onChange={handleSelectGroupChange}
              onSearch={handleSearch}
              onSubmit={handleSelectGroupSubmit}
              onExpandCollapse={handleSelectGroupExpandCollapse}
              hint={hint}
              hintNoResults={hintNoResults}
              memoSelectOption
              error={showSelectGroupFeedback}
              placeholder={intl.formatMessage({
                id: 'Load.RecipientsList.SelectOrAddGroupPlaceholder',
              })}
              data-mu-test="input-group"
              forceCollapse={forceCollapse}
            />
          </FormElement>

          <Checkbox
            checked={optSetAsTestGroup}
            data-mu-test="opt-set-as-test-group"
            label={intl.formatMessage({
              id: 'Load.RecipientsList.SetTargetAsTestGroup',
            })}
            onChange={toggleOptSetAsTestGroup}
          />
        </Flex>
      </Collapsible>
    </Flex>
  )
}

export default InputAddToGroup
